import * as React from "react";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openRiskFormWindow } from "./windowManager/RiskFormWindow";
import { displayContactName } from "../utils/contactDisplay";
import { Dismiss20Regular, ChevronRight16Regular } from "@fluentui/react-icons";
import { RISIKO_ARTEN as TYPES, type NewRiskType, type RisikoArtSpec as TypeSpec } from "./risikoArten";
import { BRAND } from "./risikoanalyseStep/tokens";
import { ContactSelector } from "./ContactSelector";

// Katalog der Risikoarten: seit 28.09.2026 in ./risikoArten (geteilt mit dem
// „+“-Menü im App-Header, components/neuAnlage/NeuAnlageDialog.tsx).
export type { NewRiskType } from "./risikoArten";


/* ──────────────────────────────────────────────────────────────────────────
   Styles — plain inline objects (no makeStyles, no Fluent components)
   ────────────────────────────────────────────────────────────────────────── */

const s = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    backgroundColor: "rgba(20,8,20,0.55)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 16,
  },
  panel: {
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    width: "min(760px, 100%)",
    maxHeight: "min(720px, 92vh)",
    display: "flex",
    flexDirection: "column" as const,
    borderRadius: 14,
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 24px 60px rgba(20,8,20,0.32)",
    overflow: "hidden",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    borderBottom: `1px solid ${BRAND.borderDashed}`,
  },
  headLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: BRAND.muted,
    minWidth: 0,
    flex: 1,
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    fontFamily: BRAND.fontAccent,
    color: BRAND.ink,
  },
  crumbBtn: {
    background: "none",
    border: "none",
    padding: 0,
    color: BRAND.muted,
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  crumbCurrent: {
    color: BRAND.ink,
    fontWeight: 600,
  },
  closeBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "none",
    backgroundColor: "transparent",
    color: BRAND.muted,
    cursor: "pointer",
  },
  body: {
    padding: 16,
    overflowY: "auto" as const,
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: 14,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    color: BRAND.muted,
    marginBottom: 6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
  },
  tile: {
    position: "relative" as const,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: 10,
    padding: "14px 14px",
    minHeight: 110,
    color: BRAND.white,
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    textAlign: "left" as const,
    fontFamily: "inherit",
    overflow: "hidden",
    transition: "transform 120ms ease, box-shadow 120ms ease",
  },
  tileWide: {
    gridColumn: "span 3",
  },
  tileIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.18)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.white,
    flexShrink: 0,
  },
  tileLabel: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.25,
    color: BRAND.white,
  },
  tileHint: {
    fontSize: 11,
    color: "rgba(255,255,255,0.85)",
    lineHeight: 1.3,
  },
  tileSoonBadge: {
    position: "absolute" as const,
    top: 8,
    right: 8,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    padding: "2px 6px",
    borderRadius: 999,
  },
  subRiskFooter: {
    width: "100%",
    marginTop: 4,
    paddingTop: 8,
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 6,
    borderTop: "1px solid rgba(255,255,255,0.28)",
  },
  subRiskBox: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1.15,
    padding: "4px 4px",
    borderRadius: 6,
    backgroundColor: "rgba(255,255,255,0.14)",
    color: BRAND.white,
    textAlign: "center" as const,
    border: "1px solid rgba(255,255,255,0.18)",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background-color 120ms ease",
  },
  contactCard: {
    backgroundColor: BRAND.white,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 10,
    padding: 14,
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  contactHint: {
    fontSize: 12,
    color: BRAND.muted,
    lineHeight: 1.4,
  },
  selectedRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    backgroundColor: "#f5f1ee",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: BRAND.ink,
  },
};

/* ──────────────────────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────────────────────── */

type Props = {
  open: boolean;
  onClose: () => void;
  /** If provided, skip the contact-pick step. */
  contactId?: number;
  /** Optional display name for the preselected contact — used in window titles. */
  contactName?: string;
  /** Optional pre-selected risk type — skip the tile step. */
  initialType?: NewRiskType;
  /**
   * Wenn gesetzt, übernimmt der Aufrufer das Öffnen der Erfassung — der Dialog
   * öffnet dann KEIN eigenes Fenster. Genutzt aus der Vollseite heraus: dort
   * ersetzt die Auswahl die laufende Instanz, statt ein zweites Fenster zu
   * öffnen.
   */
  onPicked?: (
    riskType: NewRiskType,
    contactId: number | null,
    contactName: string | null,
  ) => void;
};

const NewRiskDialog: React.FC<Props> = ({
  open,
  onClose,
  contactId: presetContactId,
  contactName: presetContactName,
  initialType,
  onPicked,
}) => {
  const { openWindow } = useWindowManager();

  const [selectedType, setSelectedType] = React.useState<NewRiskType | null>(
    initialType ?? null,
  );
  const [contactId, setContactId] = React.useState<number | null>(
    presetContactId ?? null,
  );
  const [selectedContact, setSelectedContact] = React.useState<{
    id: number;
    label: string;
  } | null>(null);

  React.useEffect(() => {
    if (open) {
      setSelectedType(initialType ?? null);
      setContactId(presetContactId ?? null);
      setSelectedContact(null);
    }
  }, [open, initialType, presetContactId]);

  // Lock background scroll while open.
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const navigateToForm = (type: NewRiskType, cid: number | null) => {
    const name = selectedContact?.label ?? presetContactName ?? null;
    if (onPicked) {
      // BEWUSST ohne onClose(): der Aufrufer ersetzt die komplette Instanz.
      // Ein vorgeschaltetes onClose() würde dort als „ohne Auswahl geschlossen"
      // gewertet und die Erfassung schließen, bevor die Auswahl ankommt.
      onPicked(type, cid, name);
      return;
    }
    onClose();
    openRiskFormWindow(openWindow, {
      riskType: type,
      contactId: cid,
      contactName: name,
      initialSize: { width: 1080, height: 720 },
    });
  };

  const handleTypePick = (t: TypeSpec) => {
    if (t.comingSoon) return;
    setSelectedType(t.key);
    // Contact has already been chosen (or preset) at this point in the new flow.
    navigateToForm(t.key, contactId);
  };

  const handleSubRiskPick = (type: NewRiskType) => {
    setSelectedType(type);
    navigateToForm(type, contactId);
  };

  const handleContactSelect = (c: { id: number; label: string }) => {
    setSelectedContact(c);
    setContactId(c.id);
  };

  if (!open) return null;

  // Two-step flow: Contact → Risikoart.
  // If initialType is given, we skip everything and navigate directly.
  const showContactPick = contactId == null;
  const showTypePick = contactId != null && selectedType == null;

  return (
    <div
      style={s.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Neues Risiko"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={s.panel}>
        <div style={s.head}>
          <div style={s.headLeft}>
            <h2 style={s.title}>Risiken erfassen</h2>
            {selectedContact && (
              <>
                <ChevronRight16Regular style={{ color: BRAND.muted }} />
                <button
                  type="button"
                  style={s.crumbBtn}
                  onClick={() => {
                    setSelectedContact(null);
                    setContactId(presetContactId ?? null);
                    setSelectedType(null);
                  }}
                >
                  Kontakt
                </button>
                <ChevronRight16Regular style={{ color: BRAND.muted }} />
                <span style={s.crumbCurrent}>{selectedContact.label}</span>
              </>
            )}
          </div>
          <button type="button" style={s.closeBtn} onClick={onClose} aria-label="Schließen">
            <Dismiss20Regular />
          </button>
        </div>

        <div style={s.body}>
          {/* Step 1: pick contact */}
          {showContactPick && (
            <div style={s.contactCard}>
              <div style={s.sectionLabel}>Kontakt wählen</div>
              <div style={s.contactHint}>
                Bitte wählen Sie zuerst den Kontakt, für den ein Risiko erfasst
                werden soll. Anschließend wählen Sie die Kategorie und den
                konkreten Risikotyp.
              </div>
              <ContactSelector
                label="Kontakt"
                placeholder="Kontakt suchen (Name, Firma)…"
                onSelect={(c: any) => {
                  if (!c?.id) return;
                  const id = Number(c.id);
                  const label = displayContactName(c) ?? `Kontakt ${id}`;
                  handleContactSelect({ id, label });
                }}
              />
            </div>
          )}

          {/* Step 2: pick risk type — all risks shown */}
          {showTypePick && (
            <>
              <div style={s.sectionLabel}>Risikoart wählen</div>
              <div style={s.grid}>
                {TYPES.map((t) => {
                  const wide = !!t.subRisks && t.subRisks.length > 0;
                  const Icon = t.Icon;
                  const disabled = !!t.comingSoon;
                  const tileStyle: React.CSSProperties = {
                    ...s.tile,
                    ...(wide ? s.tileWide : null),
                    backgroundColor: t.color,
                    opacity: disabled ? 0.55 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                  };
                  const onTileActivate = () => {
                    if (disabled) return;
                    handleTypePick(t);
                  };
                  return (
                    <div
                      key={t.key}
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      aria-disabled={disabled}
                      onClick={onTileActivate}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onTileActivate();
                        }
                      }}
                      title={t.hint && t.hint.includes("\n") ? t.hint : undefined}
                      style={tileStyle}
                      onMouseEnter={(e) => {
                        if (disabled) return;
                        (e.currentTarget as HTMLDivElement).style.transform = "translateY(-1px)";
                        (e.currentTarget as HTMLDivElement).style.boxShadow = "0 12px 22px rgba(45,20,43,0.18)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                        (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                      }}
                    >
                      {t.comingSoon && <span style={s.tileSoonBadge}>bald</span>}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
                        <span style={s.tileIcon}>
                          <Icon />
                        </span>
                      </div>
                      <span style={s.tileLabel}>{t.label}</span>
                      {t.hint && !t.hint.includes("\n") && (
                        <span style={s.tileHint}>{t.hint}</span>
                      )}
                      {t.subRisks && t.subRisks.length > 0 && (
                        <div style={s.subRiskFooter}>
                          {t.subRisks.map((sub) => (
                            <button
                              key={sub.label}
                              type="button"
                              style={s.subRiskBox}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubRiskPick(sub.type);
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,255,255,0.28)";
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,255,255,0.14)";
                              }}
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default NewRiskDialog;
